import { Box, Grid, TextField, MenuItem, Button, Typography, IconButton, Paper } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useState } from 'react';

/**
 * charges: saved/added charges for this booking (array of { type, name, amount, description }).
 * otherCharges: available charge-type options to choose from (array of { key, label, description }).
 */
const OtherChargesSection = ({ charges, onChange, otherCharges = [] }) => {
    const [newCharge, setNewCharge] = useState({
      type: '',
      amount: '',
      description: ''
    });

    // Normalize otherCharges to array (API may return object or array)
    const list = Array.isArray(otherCharges)
      ? otherCharges
      : (otherCharges && typeof otherCharges === 'object' && !Array.isArray(otherCharges)
          ? Object.values(otherCharges)
          : []);
    // Only show types not already added (by type/key or by label/name for existing data)
    const availableChargeTypes = list.filter(
      (charge) => !charges.some((c) => c.type === charge.key || c.name === charge.label)
    );

    const handleTypeChange = (e) => {
      const selectedType = e.target.value;
      const selectedCharge = list.find(c => c.key === selectedType);
      
      setNewCharge({
        ...newCharge,
        type: selectedType,
        description: selectedCharge?.description || ''
      });
    };

    const handleAmountChange = (e) => {
      const value = e.target.value;
      setNewCharge({
        ...newCharge,
        amount: value
      });
    };

    const handleAddCharge = () => {
      if (newCharge.type && newCharge.amount) {
        const amount = Math.round(Number(newCharge.amount));
        const selected = list.find(c => c.key === newCharge.type);
        onChange([
          ...charges,
          {
            type: newCharge.type,
            name: selected?.label || newCharge.type,
            amount: amount,
            description: newCharge.description || ''
          }
        ]);
        setNewCharge({ type: '', amount: '', description: '' });
      }
    };

    const handleRemoveCharge = (index) => {
      const updatedCharges = charges.filter((_, i) => i !== index);
      onChange(updatedCharges);
    };

    return (
      <Box>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={7}>
            <TextField
              select
              label="Charge Type"
              value={newCharge.type}
              onChange={handleTypeChange}
              fullWidth
              size="small"
              SelectProps={{
                MenuProps: {
                  disablePortal: false,
                  sx: { zIndex: 1400 }
                },
                displayEmpty: true,
                renderValue: (v) => v ? (list.find(c => c.key === v)?.label || v) : ''
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: '2px'
                  }
                }
              }}
            >
              <MenuItem value="">
                <em>Select charge type</em>
              </MenuItem>
              {availableChargeTypes.map((charge) => (
                <MenuItem key={charge.key} value={charge.key}>
                  {charge.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Amount"
              type="number"
              value={newCharge.amount}
              onChange={handleAmountChange}
              fullWidth
              size="small"
              inputProps={{ 
                step: 100, 
                min: 0,
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: '2px'
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button
              variant="contained"
              onClick={handleAddCharge}
              disabled={!newCharge.type || !newCharge.amount}
              size="small"
              sx={{ 
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: '60px'
              }}
            >
              Add
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={newCharge.description}
              onChange={(e) => setNewCharge({ ...newCharge, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: '2px'
                  }
                }
              }}
            />
            
          </Grid>
        </Grid>

        {/* Display existing charges */}
        {charges.length > 0 && (
          <Box mt={1.5}>
            <Typography variant="body2" gutterBottom sx={{ fontSize: '0.875rem' }}>
              Added Charges
            </Typography>
            <Grid container spacing={1}>
            {charges.map((charge, index) => (
              <Grid item xs={12} key={index}>
                <Paper
                  variant="outlined"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    p: 0.75,
                    borderRadius: 1,
                    bgcolor: 'grey.50'
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.875rem' }}>
                      {charge.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      {charge.description}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mx: 1.5, minWidth: 70, textAlign: 'right', fontWeight: 500 }}>
                    ₹{Number(charge.amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => handleRemoveCharge(index)}
                    color="error"
                    sx={{ p: 0.5 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              </Grid>
            ))}
            </Grid>
          </Box>
        )}
      </Box>
    );
  };

export default OtherChargesSection;